import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Loader2, Check } from "lucide-react";
import { api } from "@/lib/api";
import { ArtThumb } from "@/components/riptag/TrackTable";

export const MbMatchPanel = ({ sessionId, tracks, album: albumScope, onApplied }) => {
  const [artist, setArtist] = useState("");
  const [album, setAlbum] = useState("");
  const [candidates, setCandidates] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState("");

  useEffect(() => {
    if (albumScope) {
      setArtist(albumScope.is_compilation ? "" : albumScope.artist || "");
      setAlbum(albumScope.title || "");
      setCandidates(null);
      return;
    }
    const first = tracks.find((t) => t.artist || t.album);
    if (first) {
      setArtist((a) => a || first.artist || "");
      setAlbum((al) => al || first.album || "");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [albumScope?.album_id, tracks]);

  const search = async () => {
    if (!album.trim()) { toast.error("Enter an album name to search"); return; }
    setBusy(true);
    try {
      const r = await api.get("/rip/mb/search", { params: { artist, album } });
      setCandidates(r.data.candidates || []);
      if (!r.data.candidates?.length) toast.info("No MusicBrainz matches — check spelling or tag manually");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "MusicBrainz search failed");
    }
    setBusy(false);
  };

  const apply = async (c) => {
    setApplying(c.release_id);
    try {
      const r = albumScope
        ? await api.post(`/rip/albums/${albumScope.album_id}/apply-mb`, {
            release_id: c.release_id, confidence: c.score })
        : await api.post(`/rip/sessions/${sessionId}/apply-mb`, {
            release_id: c.release_id, confidence: c.score });
      toast.success(`Applied "${r.data.release}" metadata to ${r.data.updated} tracks${
        albumScope ? ` in "${albumScope.title}"` : ""}`);
      onApplied();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    }
    setApplying("");
  };

  return (
    <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="rip-mb-panel">
      <div className="text-xs font-mono uppercase tracking-wider text-ink-muted mb-2">
        Identify with MusicBrainz
        {albumScope && <span className="text-brand-amber normal-case tracking-normal"> — applies only to "{albumScope.title}"</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        <input value={artist} onChange={(e) => setArtist(e.target.value)} placeholder="Artist"
          data-testid="rip-mb-artist-input"
          className="flex-1 min-w-[160px] rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm focus:border-brand-amber focus:outline-none" />
        <input value={album} onChange={(e) => setAlbum(e.target.value)} placeholder="Album"
          data-testid="rip-mb-album-input"
          onKeyDown={(e) => e.key === "Enter" && search()}
          className="flex-1 min-w-[160px] rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm focus:border-brand-amber focus:outline-none" />
        <button onClick={search} disabled={busy} data-testid="rip-mb-search-btn"
          className="inline-flex items-center gap-2 rounded-md bg-brand-amber text-black px-4 py-2 text-sm font-bold hover:opacity-90 transition disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Search
        </button>
      </div>

      {candidates && candidates.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="rip-mb-candidates">
          {candidates.map((c) => (
            <div key={c.release_id}
              className="flex items-center gap-3 rounded-lg border border-line bg-bg-elevated px-3 py-2">
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold font-mono ${
                c.score >= 90 ? "bg-emerald-500/20 text-emerald-300" : "bg-bg-surface text-ink-muted border border-line"}`}>
                {c.score}%
              </span>
              <ArtThumb url={`https://coverartarchive.org/release/${c.release_id}/front-250`}
                size="w-11 h-11" testId={`rip-mb-art-${c.release_id}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{c.title}</div>
                <div className="text-[11px] text-ink-muted font-mono truncate">
                  {c.artist}{c.year ? ` · ${c.year}` : ""}{c.country ? ` · ${c.country}` : ""} · {c.track_count} tracks
                </div>
              </div>
              <button onClick={() => apply(c)} disabled={!!applying}
                data-testid={`rip-mb-apply-${c.release_id}`}
                className="shrink-0 inline-flex items-center gap-1 rounded-full border border-brand-cyan/60 text-brand-cyan px-3 py-1 text-xs font-bold hover:bg-brand-cyan/10 transition disabled:opacity-50">
                {applying === c.release_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                Use this
              </button>
            </div>
          ))}
          <div className="text-[10px] text-ink-muted font-mono">
            {albumScope
              ? "Applies metadata only to the selected album's tracks — other albums in this session stay untouched."
              : "No album selected — applies to ALL session tracks in order. Select an album above for precise matching."}
          </div>
        </div>
      )}
    </div>
  );
};
